import React from "react";

import {
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@material-ui/core";

import { parseCustomFieldValue } from "../../../../components/common/CustomFields";

import { CustomFieldsDisplay, Unit } from "../../../../utils/constants";
import { trimDecimal } from "../../../../utils/utils";
import {
  calculateMbf,
  calculateMsf,
  getPPU,
  getUnitName,
  isLumberCommodity,
  isPanelsCommodity,
} from "../../../../utils/loadTypeUtils";
import {
  AttachmentsBlock,
  HeaderBlock,
  InfoBlock,
  LogisticsBlock,
  NotesBlock,
  PhotosBlock,
  SealsBlock,
} from "./OutboundPOSBody";
import { calcTotalQuantity } from "../OutboundUtils";

const LineItemsBlock = ({ record, posPickList }) => {
  const inventoryCustomFields = (
    record?.account?.inventoryCustomFields || []
  ).filter((field) => field.display.includes(CustomFieldsDisplay.POS));

  const showProductCodeColumn = posPickList.some((lineItem) =>
    lineItem.inventory.productCode?.trim(),
  );
  const showPCSColumn = posPickList.some(
    (lineItem) => lineItem.inventory.pcs != null,
  );
  const pcsColumnName = record.totalUOM === Unit.UNIT ? "PPU" : "Pieces";
  const showFootageColumn = posPickList.some(
    (lineItem) => lineItem.inventory.footage != null,
  );

  return (
    <div style={{ width: "100%" }}>
      <Divider />
      <h3>
        <strong> INVENTORY SHIPPED </strong>
      </h3>
      <Table key="outboundInventory" style={{ width: "100%" }}>
        <TableHead>
          <TableRow>
            {showProductCodeColumn && (
              <TableCell
                style={{ textAlign: "left", padding: "3px", width: "15%" }}
              >
                Product Code
              </TableCell>
            )}
            <TableCell
              style={{
                textAlign: "left",
                padding: "3px",
                width: `${showProductCodeColumn ? "25%" : "35%"}`, // Adjust dynamically based on Product Code visibility
              }}
            >
              Product Name
            </TableCell>
            <TableCell
              style={{ textAlign: "right", padding: "3px", width: "10%" }}
            >
              {getUnitName(record.totalUOM)}
            </TableCell>
            {showPCSColumn && (
              <TableCell
                style={{ textAlign: "right", padding: "3px", width: "10%" }}
              >
                {pcsColumnName}
              </TableCell>
            )}
            {showFootageColumn && (
              <TableCell
                style={{ textAlign: "right", padding: "3px", width: "10%" }}
              >
                Footage
              </TableCell>
            )}
            <TableCell
              style={{ textAlign: "right", padding: "3px", width: "10%" }}
            >
              Order#
            </TableCell>
            {inventoryCustomFields.map((field, index) => (
              <TableCell
                key={"inv_cf_h_" + index}
                style={{
                  textAlign: "center",
                  padding: "3px",
                  width: `${100 / (inventoryCustomFields.length + 5)}%`, // Adjust dynamically
                }}
              >
                {field.name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {posPickList.map((lineItem) => (
            <TableRow key={lineItem.id}>
              {showProductCodeColumn && (
                <TableCell
                  style={{ textAlign: "left", padding: "3px", width: "15%" }}
                >
                  {lineItem.inventory.productCode
                    ? lineItem.inventory.productCode
                    : "--"}
                </TableCell>
              )}
              <TableCell
                style={{
                  textAlign: "left",
                  padding: "3px",
                  width: `${showProductCodeColumn ? "25%" : "35%"}`, // Adjust dynamically
                }}
              >
                {lineItem.inventory.productName}
              </TableCell>
              <TableCell
                style={{ textAlign: "right", padding: "3px", width: "10%" }}
              >
                {lineItem.totalQuantityToPick}
              </TableCell>
              {showPCSColumn && (
                <TableCell
                  style={{ textAlign: "right", padding: "3px", width: "10%" }}
                >
                  {lineItem.inventory.pcs != null
                    ? lineItem.inventory.pcs
                    : "--"}
                </TableCell>
              )}
              {showFootageColumn && (
                <TableCell
                  style={{ textAlign: "right", padding: "3px", width: "10%" }}
                >
                  {lineItem.inventory.footage
                    ? `${trimDecimal(
                        lineItem.inventory.footage *
                          lineItem.totalQuantityToPick,
                      ).toLocaleString()} ${lineItem.inventory.footageUOM}`
                    : "--"}
                </TableCell>
              )}
              <TableCell
                style={{ textAlign: "right", padding: "3px", width: "10%" }}
              >
                {lineItem.inventory.poNumber}
              </TableCell>
              {inventoryCustomFields.map((field, index) => {
                const customFieldValue = lineItem.inventory.customFields?.find(
                  (cf) => cf.startsWith(field.name),
                );

                return (
                  <TableCell
                    key={"inv_cf_" + index}
                    style={{
                      textAlign: "center",
                      padding: "3px",
                      width: `${100 / (inventoryCustomFields.length + 5)}%`, // Adjust dynamically
                    }}
                  >
                    {parseCustomFieldValue(customFieldValue)}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const TotalsBlock = ({ record, posPickList }) => {
  const totalQuantity = calcTotalQuantity(record);

  // PCS Totals and Flags
  let totalPCS = 0;
  let allMissingPCS = true;
  let somePCSNull = false;

  // MBF Totals and Flags
  let totalMBF = 0;
  let allMissingMBF = true;
  let someMBFNull = false;

  // MSF Totals and Flags
  let totalMSF = 0;
  let allMissingMSF = true;
  let someMSFNull = false;

  posPickList.forEach((lineItem) => {
    // PCS Calculation
    if (lineItem.inventory.pcs == null) {
      somePCSNull = true;
    } else {
      allMissingPCS = false;
      if (record.totalUOM === Unit.UNIT) {
        totalPCS += lineItem.inventory.pcs * lineItem.totalQuantityToPick;
      } else {
        totalPCS += lineItem.inventory.pcs;
      }
    }

    // Footage Calculations
    if (lineItem.inventory.footageUOM === Unit.MBF) {
      if (lineItem.inventory.footage == null) {
        someMBFNull = true;
      } else {
        allMissingMBF = false;
        totalMBF += lineItem.inventory.footage * lineItem.totalQuantityToPick;
      }
    } else if (lineItem.inventory.footageUOM === Unit.MSF) {
      if (lineItem.inventory.footage == null) {
        someMSFNull = true;
      } else {
        allMissingMSF = false;
        totalMSF += lineItem.inventory.footage * lineItem.totalQuantityToPick;
      }
    }
  });

  return (
    <div style={{ width: "100%" }}>
      <Divider />
      <Table key="outboundTotals" style={{ width: "100%" }}>
        <TableBody>
          <TableRow key="totalUOM">
            <TableCell
              style={{ textAlign: "right", minWidth: "66%", padding: "3px" }}
            >
              Total {getUnitName(record.totalUOM)}
            </TableCell>
            <TableCell
              style={{ textAlign: "right", minWidth: "33%", padding: "3px" }}
            >
              {totalQuantity}
            </TableCell>
          </TableRow>
          {!allMissingPCS && (
            <TableRow key="totalPCS">
              <TableCell
                style={{ textAlign: "right", minWidth: "66%", padding: "3px" }}
              >
                Total Pieces
              </TableCell>
              <TableCell
                style={{ textAlign: "right", minWidth: "33%", padding: "3px" }}
              >
                {somePCSNull ? "N/A" : trimDecimal(totalPCS).toLocaleString()}
              </TableCell>
            </TableRow>
          )}
          {!allMissingMBF && (
            <TableRow key="totalMBF">
              <TableCell
                style={{ textAlign: "right", minWidth: "66%", padding: "3px" }}
              >
                Total MBF
              </TableCell>
              <TableCell
                style={{ textAlign: "right", minWidth: "33%", padding: "3px" }}
              >
                {someMBFNull
                  ? "N/A"
                  : `${trimDecimal(totalMBF).toLocaleString()} MBF`}
              </TableCell>
            </TableRow>
          )}
          {!allMissingMSF && (
            <TableRow key="totalMSF">
              <TableCell
                style={{ textAlign: "right", minWidth: "66%", padding: "3px" }}
              >
                Total MSF
              </TableCell>
              <TableCell
                style={{ textAlign: "right", minWidth: "33%", padding: "3px" }}
              >
                {someMSFNull
                  ? "N/A"
                  : `${trimDecimal(totalMSF, 3).toLocaleString()} MSF`}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export const LumberPOSBody = ({ record, enclosePhotos, encloseDocs }) => {
  const posPickList = record.shipmentLineItems.items.map((lineItem) => {
    const loadType =
      lineItem.inventory?.reloadYardCommodityId || record.reloadYardCommodityId;

    let footage = null;
    let footageUOM = "";

    if (isLumberCommodity(loadType)) {
      footage = calculateMbf(lineItem.inventory);
      footageUOM = Unit.MBF;
    } else if (isPanelsCommodity(loadType)) {
      footage = calculateMsf(lineItem.inventory);
      footageUOM = Unit.MSF;
    }

    return {
      ...lineItem,
      inventory: {
        ...lineItem.inventory,
        pcs: getPPU(lineItem.inventory),
        footage,
        footageUOM,
      },
    };
  });

  return (
    <Paper elevation={0} style={{ maxWidth: "900px", padding: "10px" }}>
      <HeaderBlock />
      <InfoBlock record={record} />
      <LogisticsBlock record={record} />
      <LineItemsBlock record={record} posPickList={posPickList} />
      <TotalsBlock record={record} posPickList={posPickList} />
      <SealsBlock record={record} />
      <NotesBlock record={record} />
      {encloseDocs && <AttachmentsBlock record={record} />}
      {enclosePhotos && <PhotosBlock record={record} />}
    </Paper>
  );
};
